import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Button, Tooltip, CircularProgress, Typography, FormControl,
} from '@mui/material';
import OtpInput from 'react-otp-input';
import 'react-phone-input-2/lib/style.css';
import PhoneInputComponent from './PhoneInputComponent';
import { UrlConstant } from '../../../../../commom/UrlConstant';
import Axios from '../../../../../config/axios';
import { FormField, getFieldWidth } from '../utils/formFieldTypes';
import { renderFieldLabel } from '../utils';

interface PhoneOTPProps {
  field: FormField;
  selectedValues: Record<number, any>;
  setSelectedValues: (updater: (prev: any) => any) => void;
  validationErrors: Record<number, string>;
  onChange: (field: any) => void;
  showErrorPopup: (heading: string, message: string) => void;
}

const PhoneOTPComponent: React.FC<PhoneOTPProps> = ({
  field, selectedValues, setSelectedValues, validationErrors, onChange, showErrorPopup,
}) => {
  const [isOTPMode, setIsOTPMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [otpError, setOtpError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  const phoneSnapshot = useRef<string>('');

  const phoneValue = selectedValues[field.id] || '';
  const otpValue = selectedValues[`${field.id}_otp`] || '';
  const isVerified = selectedValues[`${field.id}_verified`] || false;

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handlePhoneChange = (value: string, data: any) => {
    setSelectedValues((prev) => ({
      ...prev,
      [field.id]: value,
      [`${field.id}_verified`]: false,
    }));
    onChange({ ...field, value: `+${value}` });
  };

  const startTimer = () => {
    setTimer(60);
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = useCallback(async () => {
    if (!phoneValue || phoneValue.length < 10) return;
    setIsLoading(true);
    try {
      const response = await Axios.post(UrlConstant.Auth.sendPhoneOTP, { phone: phoneValue });

      if (response?.data?.status === 'success') {
        setIsOTPMode(true);
        startTimer();
      } else {
        const errMsg = response?.data?.message?.[0] || response?.data?.msg || 'Unable to send OTP. Please try again.';
        showErrorPopup('OTP Send Failed', errMsg);
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.message?.[0] || err?.response?.data?.msg || 'Unable to send OTP. Please try again.';
      showErrorPopup('OTP Send Failed', errMsg);
    } finally {
      setIsLoading(false);
    }
  }, [phoneValue, showErrorPopup]);

  const handleVerifyOTP = useCallback(async () => {
    if (!otpValue || otpValue.length !== 6) return;

    setIsLoading(true);
    try {
      const response = await Axios.post(UrlConstant.Auth.verifyPhoneOTP, {
        otp: otpValue,
        phone: phoneValue,
      });

      if (response?.data?.status === 'success' && response?.data?.data?.verified) {
        setSelectedValues((prev) => ({
          ...prev,
          [`${field.id}_verified`]: true,
          [`${field.id}_otp`]: '',
        }));
        setIsOTPMode(false);
        setIsEditMode(false);
        phoneSnapshot.current = '';
        onChange({ ...field, value: phoneValue, verified: true });
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.message?.[0] || err?.response?.data?.msg || 'An error occurred while verifying the OTP.';
      setOtpError('Verification failed');
      showErrorPopup('OTP Verification Failed', errMsg);
    } finally {
      setIsLoading(false);
    }
  }, [otpValue, phoneValue, field, onChange, setSelectedValues, showErrorPopup]);
  const handleEditStart = useCallback(() => {
    phoneSnapshot.current = phoneValue;
    setIsEditMode(true);
  }, [phoneValue]);

  const handleCancelEdit = useCallback(() => {
    const previousPhone = phoneSnapshot.current;

    setSelectedValues((prev) => ({
      ...prev,
      [field.id]: previousPhone || prev[field.id],
      [`${field.id}_otp`]: '',
      [`${field.id}_verified`]: !!previousPhone,
    }));

    setIsOTPMode(false);
    setIsEditMode(false);

    if (timerRef.current) clearInterval(timerRef.current);
    setTimer(0);

    phoneSnapshot.current = '';

    onChange({
      ...field,
      value: previousPhone || phoneValue,
      verified: !!previousPhone,
    });
  }, [field, setSelectedValues, onChange, phoneValue]);

  const errorMessage = validationErrors[field.id];
  const hasError = !!errorMessage;


  if (isVerified && !isEditMode && !isOTPMode && !hasError) {
    return (
      <FormControl fullWidth margin='normal'>
        {renderFieldLabel(field)}
        <Box display='flex' gap='10px' alignItems='center'>
          <Box sx={{ width: getFieldWidth('phone') }}>
            <PhoneInputComponent
              field={field}
              value={phoneValue}
              onChange={handlePhoneChange}
              disabled={true}
              errorMessage=''
              hideLabel={true}
              containerStyle={{ margin: '0' }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '1px solid #4EDF6D',
              borderRadius: '50px',
              padding: '0 16px',
              height: '48px',
              whiteSpace: 'nowrap',
            }}
          >
            <i className='fa-solid fa-circle-check' style={{ color: '#4EDF6D', fontSize: '14px' }} />
            <Typography sx={{ color: '#4EDF6D', fontWeight: 'bold', fontSize: '14px' }}>
              Verified
            </Typography>

            <Box sx={{ width: '1px', height: '20px', bgcolor: '#4EDF6D40', mx: '4px' }} />

            <Typography
              component='span'
              onClick={handleEditStart}
              sx={{
                color: '#a3a3a3',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: '500',
                '&:hover': { color: '#f2674a' },
                transition: 'color 0.2s',
              }}
            >
              Edit
            </Typography>
          </Box>
        </Box>
      </FormControl>
    );
  }

  if (!isOTPMode) {
    return (
      <FormControl fullWidth margin='normal'>
        {renderFieldLabel(field)}
        <Box sx={{ width: '100%' }}>
          <Box display='flex' gap='10px' alignItems='center'>
            <Box sx={{ width: getFieldWidth('phone') }}>
              <PhoneInputComponent
                field={field}
                value={phoneValue}
                onChange={handlePhoneChange}
                disabled={false}
                // errorMessage={errorMessage}
                hideLabel={true}
                showDynamicHint={true}
                containerStyle={{ margin: '0' }}
              />
            </Box>

            <Box display='flex' gap='8px'>
              <Button
                onClick={handleSendOTP}
                disabled={isLoading || !phoneValue || phoneValue.length < 10}
                sx={{
                  color: 'white',
                  bgcolor: '#f2674a',
                  padding: '13px 24px',
                  borderRadius: '50px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  height: '48px',
                  '&:hover': { bgcolor: '#e85c3e' },
                  '&:disabled': { bgcolor: '#a3a3a3', color: '#666' },
                }}
              >
                {isLoading ? <CircularProgress size={20} color='inherit' /> : 'Send OTP'}
              </Button>

              {isEditMode && (
                <Button
                  onClick={handleCancelEdit}
                  sx={{
                    color: '#a3a3a3',
                    bgcolor: 'transparent',
                    border: '1px solid #a3a3a3',
                    padding: '13px 20px',
                    borderRadius: '50px',
                    fontWeight: 'bold',
                    height: '48px',
                    '&:hover': { borderColor: '#f2674a', color: '#f2674a' },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Box>

          {errorMessage && (
            <div style={{ color: '#d32f2f', marginTop: '8px', fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <i className='fa-solid fa-exclamation-circle' style={{ fontSize: '11px' }} />
              {errorMessage}
            </div>
          )}
        </Box>
      </FormControl>
    );
  }

  return (
    <Box sx={{ width: '100%', margin: '16px 0', flex: '0 0 100%' }}>
      <label className='text-secondary' style={{ marginBottom: '10px', display: 'block' }}>
        <span>
          Enter OTP
          {field.hint && (
            <Tooltip title={field.hint} arrow placement='top'>
              <i className='fa-solid fa-info-circle' style={{ color: '#FFFFFF', marginLeft: '8px', fontSize: '1rem', cursor: 'help' }} />
            </Tooltip>
          )}
        </span>
      </label>

      <Typography variant='body2' sx={{ color: '#a3a3a3', mb: '10px', fontWeight: '400' }}>
        We've sent a verification code to {phoneValue}
      </Typography>
      <OtpInput
        value={otpValue}
        onChange={(value) => {
          if (/^\d{0,6}$/.test(value)) {
            setOtpError('');
            setSelectedValues((prev) => ({
              ...prev,
              [`${field.id}_otp`]: value,
            }));
          }
        }}
        numInputs={6}
        inputType='tel'
        shouldAutoFocus
        containerStyle={{ display: 'flex', width: '100%', justifyContent: 'flex-start', gap: '10px', margin: '0 0 8px 0' }}
        inputStyle={{
          width: '48px', height: '48px', fontSize: '18px', borderRadius: '8px',
          border: `1px solid ${otpError ? '#d32f2f' : '#a3a3a3'}`,
          textAlign: 'center', color: '#fff', background: 'transparent', fontWeight: '600',
        }}
        renderInput={(props) => (
          <input
            {...props}
            className='otp'
            onKeyDown={(e) => { if (!/[0-9]/.test(e.key) && e.key !== 'Backspace') e.preventDefault(); }}
            style={{ ...props.style, fontFamily: 'monospace' }}
          />
        )}
      />

      <Box display='flex' gap='10px' alignItems='center' mt='15px'>
        <Button
          onClick={handleVerifyOTP}
          disabled={isLoading || otpValue.length !== 6}
          sx={{
            color: 'white', bgcolor: '#f2674a', padding: '13px 40px',
            borderRadius: '50px', fontWeight: 'bold', minWidth: '120px',
            '&:hover': { bgcolor: '#e85c3e' },
            '&:disabled': { bgcolor: '#a3a3a3', color: '#666' },
          }}
        >
          {isLoading ? <CircularProgress size={20} color='inherit' /> : 'Verify'}
        </Button>

        {(isEditMode || isOTPMode) && (
          <Button
            onClick={handleCancelEdit}
            sx={{
              color: '#a3a3a3', bgcolor: 'transparent', border: '1px solid #a3a3a3',
              padding: '13px 20px', borderRadius: '50px', fontWeight: 'bold', height: '48px',
              '&:hover': { borderColor: '#f2674a', color: '#f2674a' },
            }}
          >
            Cancel
          </Button>
        )}

        {timer > 0 ? (
          <Typography variant='body2' sx={{ color: '#a3a3a3', fontWeight: '500' }}>
            Resend in {timer}s
          </Typography>
        ) : (
          <Typography
            component='span'
            sx={{ color: '#f2674a', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', '&:hover': { textDecoration: 'underline' } }}
            onClick={handleSendOTP}
          >
            Resend OTP
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PhoneOTPComponent;